import type {
  OfficialStatusIncident,
  OfficialStatusLatestUpdate,
  OfficialStatusMaintenance,
  OfficialStatusProvider,
} from '@nadoutong/contracts';
import { useState } from 'react';
import { useOfficialProviderStatus } from '../../api/useStatus.ts';
import { formatTimestamp } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, cx, Panel, Skeleton, StatusLamp } from '../../ui/index.ts';
import {
  componentTone,
  incidentTone,
  indicatorTone,
  maintenanceTone,
  officialStatusLabel,
} from './officialStatusLabels.ts';

const LINK_CLASS =
  'underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40';

/**
 * OpenAI / Anthropic public status, mirrored by `GET /v1/status/providers`.
 *
 * Independent of the namespace timeline: a failure here (including the
 * 404 production returns until the route ships) removes this panel and
 * leaves the rest of `/status` alone. Vendor names, descriptions, and
 * update bodies stay in the vendor's language.
 *
 * Collapsed by default: each card shows only its most recent incident.
 * "Show all" expands every card to all incidents, maintenances, and
 * components.
 */
export function OfficialStatusPanel(): JSX.Element | null {
  const t = useT();
  const officialQ = useOfficialProviderStatus();
  const [expanded, setExpanded] = useState(false);

  if (officialQ.isPending) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="status-official-loading">
        <Skeleton className="h-28" />
        <Skeleton className="h-28" />
      </div>
    );
  }

  if (officialQ.isError || officialQ.data === undefined) {
    return null;
  }

  const providers = officialQ.data.providers;
  if (!Array.isArray(providers)) {
    return null;
  }

  const hasMore = providers.some(
    (provider) =>
      !provider.stale &&
      (provider.incidents.length > 1 ||
        provider.maintenances.length > 0 ||
        provider.components.length > 0),
  );

  return (
    <Panel
      caption={t('status.official.caption')}
      actions={
        hasMore ? (
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={expanded}
            onClick={() => {
              setExpanded((open) => !open);
            }}
            data-testid="status-official-expand"
          >
            {expanded ? t('status.official.collapse') : t('status.official.expand')}
          </Button>
        ) : undefined
      }
      padded
      testId="status-official-panel"
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-caption text-ink-low">{t('status.official.hint')}</p>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {providers.map((provider) => (
            <OfficialProviderCard key={provider.provider} provider={provider} expanded={expanded} />
          ))}
        </div>
      </div>
    </Panel>
  );
}

/**
 * The app each official status page maps to, with the same logo the
 * desktop app shows for it: Anthropic -> Claude, OpenAI -> Codex. The
 * files are copied from the desktop repo into `public/app-logos/`. An
 * unrecognised provider gets no logo.
 */
const APP_LOGOS = {
  anthropic: { name: 'Claude', src: '/app-logos/claude.svg' },
  openai: { name: 'Codex', src: '/app-logos/codex.png' },
} as const;

function OfficialProviderCard({
  provider,
  expanded,
}: {
  readonly provider: OfficialStatusProvider;
  readonly expanded: boolean;
}): JSX.Element {
  const t = useT();
  // A stale mirror is not current. The page indicator reads unknown, and
  // the last description, incidents, and components stay hidden.
  const shownIndicator = provider.stale ? 'unknown' : provider.indicator;
  const tone = indicatorTone(shownIndicator);
  const description = provider.stale ? null : nonEmpty(provider.description);
  const appLogo = Object.hasOwn(APP_LOGOS, provider.provider)
    ? APP_LOGOS[provider.provider as keyof typeof APP_LOGOS]
    : undefined;

  return (
    <article
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-line p-4"
      data-testid={`status-official-provider-${provider.provider}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {appLogo !== undefined ? (
          <img
            src={appLogo.src}
            alt={appLogo.name}
            title={appLogo.name}
            width={20}
            height={20}
            className="h-5 w-5 shrink-0 self-center"
            data-testid={`status-official-app-logo-${provider.provider}`}
          />
        ) : null}
        <h3 className="m-0 break-words text-heading text-ink">{provider.name}</h3>
        <StatusLamp tone={tone} size="sm" className="self-center" />
        <span
          className={cx(
            'break-words text-caption',
            tone === 'off' ? 'text-ink-low' : 'text-ink-mid',
          )}
        >
          {officialStatusLabel('indicator', shownIndicator, t)}
        </span>
      </div>

      {provider.stale ? (
        <p className="m-0 text-body text-ink-low">{t('status.official.stale')}</p>
      ) : description !== null ? (
        <p className="m-0 break-words text-body text-ink-mid">{description}</p>
      ) : null}

      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="m-0 font-mono text-caption tabular-nums text-ink-low">
          {t('status.official.checked_at', { time: formatTimestamp(provider.checked_at) })}
        </p>
        <ExternalLink
          href={provider.status_page_url}
          className={cx('text-caption font-medium text-rust-text', LINK_CLASS)}
        >
          {t('status.official.view_page')}
        </ExternalLink>
      </div>

      {provider.stale ? null : expanded ? (
        <>
          <IncidentList incidents={provider.incidents} />
          <MaintenanceList maintenances={provider.maintenances} />
          <ComponentsBlock provider={provider} />
        </>
      ) : (
        <IncidentList incidents={mostRecentIncident(provider.incidents)} />
      )}
    </article>
  );
}

function IncidentList({
  incidents,
}: {
  readonly incidents: readonly OfficialStatusIncident[];
}): JSX.Element {
  const t = useT();
  if (incidents.length === 0) {
    return <p className="m-0 text-body text-ink-low">{t('status.official.no_incidents')}</p>;
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {incidents.map((incident) => (
        <IncidentRow key={incident.id} incident={incident} />
      ))}
    </ul>
  );
}

function IncidentRow({ incident }: { readonly incident: OfficialStatusIncident }): JSX.Element {
  const t = useT();
  const resolved = incident.resolved_at !== null;
  const impact = nonEmpty(incident.impact);
  return (
    <li
      data-testid={`status-official-incident-${incident.id}`}
      className={cx(
        'flex flex-col gap-1.5 rounded border border-line-soft px-3 py-2',
        resolved ? undefined : 'bg-well',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <ExternalLink
          href={incident.url}
          className={cx(
            'min-w-0 break-words text-body font-medium',
            LINK_CLASS,
            resolved ? 'text-ink-mid' : 'text-rust-text',
          )}
        >
          {incident.name}
        </ExternalLink>
        <Chip tone={incidentTone(incident.status)} lamp={false}>
          {officialStatusLabel('incident', incident.status, t)}
        </Chip>
        {impact !== null ? (
          <span className="text-caption text-ink-low">
            {officialStatusLabel('impact', impact, t)}
          </span>
        ) : null}
      </div>
      <p className="m-0 flex flex-wrap gap-x-3 gap-y-1 font-mono text-caption tabular-nums text-ink-low">
        {incident.started_at !== null ? (
          <span>
            {t('status.official.started_at', { time: formatTimestamp(incident.started_at) })}
          </span>
        ) : null}
        {resolved ? (
          <span>
            {t('status.official.resolved')} {formatTimestamp(incident.resolved_at)}
          </span>
        ) : null}
      </p>
      <LatestUpdate update={incident.latest_update} />
    </li>
  );
}

function LatestUpdate({
  update,
}: {
  readonly update: OfficialStatusLatestUpdate | null;
}): JSX.Element | null {
  const t = useT();
  if (update === null) {
    return null;
  }
  const body = nonEmpty(update.body);
  const at = nonEmpty(update.at);
  if (body === null && at === null) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1">
      {body !== null ? (
        <p
          className="m-0 line-clamp-3 whitespace-pre-line break-words text-caption text-ink-mid"
          title={body}
        >
          {body}
        </p>
      ) : null}
      <p className="m-0 font-mono text-caption tabular-nums text-ink-low">
        {t('status.official.latest_update', { time: formatTimestamp(at) })}
      </p>
    </div>
  );
}

function MaintenanceList({
  maintenances,
}: {
  readonly maintenances: readonly OfficialStatusMaintenance[];
}): JSX.Element | null {
  if (maintenances.length === 0) {
    return null;
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {maintenances.map((maintenance) => (
        <MaintenanceRow key={maintenance.id} maintenance={maintenance} />
      ))}
    </ul>
  );
}

function MaintenanceRow({
  maintenance,
}: {
  readonly maintenance: OfficialStatusMaintenance;
}): JSX.Element {
  const t = useT();
  const impact = nonEmpty(maintenance.impact);
  return (
    <li
      data-testid={`status-official-maintenance-${maintenance.id}`}
      className="flex flex-col gap-1.5 rounded border border-line-soft px-3 py-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <ExternalLink
          href={maintenance.url}
          className={cx('min-w-0 break-words text-body font-medium text-rust-text', LINK_CLASS)}
        >
          {maintenance.name}
        </ExternalLink>
        <Chip tone={maintenanceTone(maintenance.status)} lamp={false}>
          {officialStatusLabel('maintenance', maintenance.status, t)}
        </Chip>
        {impact !== null ? (
          <span className="text-caption text-ink-low">
            {officialStatusLabel('impact', impact, t)}
          </span>
        ) : null}
      </div>
      <p className="m-0 font-mono text-caption tabular-nums text-ink-low">
        {t('status.official.scheduled', {
          start: formatTimestamp(maintenance.scheduled_for),
          end: formatTimestamp(maintenance.scheduled_until),
        })}
      </p>
    </li>
  );
}

function ComponentsBlock({
  provider,
}: {
  readonly provider: OfficialStatusProvider;
}): JSX.Element | null {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const components = provider.components;
  if (components.length === 0) {
    return null;
  }
  const nonOperational = components.filter((component) => component.status !== 'operational');
  const visible = expanded ? components : nonOperational;
  return (
    <div className="flex flex-col gap-2">
      {!expanded && nonOperational.length === 0 ? (
        <p className="m-0 text-body text-ink-low">{t('status.official.all_operational')}</p>
      ) : null}
      {visible.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {visible.map((component) => (
            <li key={component.id} className="flex flex-wrap items-center justify-between gap-2">
              <span className="min-w-0 break-words text-body text-ink">{component.name}</span>
              <Chip tone={componentTone(component.status)} lamp={false}>
                {officialStatusLabel('component', component.status, t)}
              </Chip>
            </li>
          ))}
        </ul>
      ) : null}
      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        aria-expanded={expanded}
        onClick={() => {
          setExpanded((open) => !open);
        }}
        data-testid={`status-official-components-toggle-${provider.provider}`}
      >
        {expanded
          ? t('status.official.components_hide', { count: components.length })
          : t('status.official.components_show', { count: components.length })}
      </Button>
    </div>
  );
}

function ExternalLink({
  href,
  className,
  children,
}: {
  readonly href: string;
  readonly className?: string;
  readonly children: string;
}): JSX.Element {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
}

/**
 * The one incident a collapsed card shows: an unresolved incident wins
 * over a resolved one, then the latest activity (latest update, else
 * updated, else started) wins.
 */
function mostRecentIncident(
  incidents: readonly OfficialStatusIncident[],
): readonly OfficialStatusIncident[] {
  let best: OfficialStatusIncident | undefined;
  for (const incident of incidents) {
    if (best === undefined || compareIncidents(incident, best) > 0) {
      best = incident;
    }
  }
  return best === undefined ? [] : [best];
}

function compareIncidents(a: OfficialStatusIncident, b: OfficialStatusIncident): number {
  const openA = a.resolved_at === null ? 1 : 0;
  const openB = b.resolved_at === null ? 1 : 0;
  if (openA !== openB) {
    return openA - openB;
  }
  return activityMs(a) - activityMs(b);
}

function activityMs(incident: OfficialStatusIncident): number {
  for (const iso of [incident.latest_update?.at, incident.updated_at, incident.started_at]) {
    const ms = iso === null || iso === undefined ? Number.NaN : Date.parse(iso);
    if (Number.isFinite(ms)) {
      return ms;
    }
  }
  return 0;
}

function nonEmpty(value: string | null): string | null {
  if (value === null || value.length === 0) {
    return null;
  }
  return value;
}
